import { graphql, useStaticQuery } from "gatsby";
import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import {  IconCaret } from "@upsolve/ui";

import getFilteredReviewsData from "./getFilteredReviewsData";
import Portal from "../overlays/Portal";
import ReviewViewerModal from "./ReviewViewerModal";
import ReviewTile from "./ReviewTile";

/**
 * ReviewsWall
 * - v0: Pull in 24 facebook, google reviews with side scroll
 * - v1: Fixed up layout overlays + added review overlay inspector
 * - v2: Filters, clearer side scrolling
 *
 * @component
 * @version 2
 */
const ReviewsWall = ({ maxReviews = 72 }) => {
  const reviewsDiv = useRef();
  const [reviews, setReviews] = useState([]);
  const [reviewInView, setReviewInView] = useState(null);
  const { pictures } = useStaticQuery(graphql`
    query ReviewsWallPictures {
      pictures: allContentfulReview(
        filter: { image: { id: { ne: null } } }
        sort: { fields: [date], order: ASC }
        limit: 14
      ) {
        nodes {
          image {
            altText
            image {
              fluid(maxWidth: 100) {
                ...GatsbyContentfulFluid_withWebp
              }
            }
          }
        }
      }
    }
  `);
  const getScrollAmount = () => (window.innerWidth < 720 ? 320 : Math.max(240, window.innerWidth / 2));

  // Set Reviews
  useEffect(() => {
    // Set Reviews to State
    const { reviews } = getFilteredReviewsData(pictures.nodes);
    setReviews(reviews);
  }, []);

  return (
    <StyledReviewsWall>
      <div className="reviews__controller">
        <label>Newest Facebook & Google Reviews</label>
        <div>
          <span
            className="scroll-reviews-back"
            onClick={() => reviewsDiv.current.scrollBy({ left: -getScrollAmount(), behavior: "smooth" })}
          >
            <IconCaret />
          </span>
          <span
            className="scroll-reviews-forward"
            onClick={() => reviewsDiv.current.scrollBy({ left: getScrollAmount(), behavior: "smooth" })}
          >
            <IconCaret />
          </span>
        </div>
      </div>
      <div className="reviews" ref={reviewsDiv}>
        {reviews
          .map((review, reviewIndex) => (
            <ReviewTile key={reviewIndex} review={review} setReviewInView={setReviewInView} />
          ))}
      </div>
      {reviewInView && (
        <Portal>
          <ReviewViewerModal review={reviewInView} onClose={() => setReviewInView(null)} />
        </Portal>
      )}
    </StyledReviewsWall>
  );
};

const StyledReviewsWall = styled.div`
  width: 100%;
  background: ${(props) => props.theme.colors.white[900]};
  overflow: hidden;
  .reviews__controller {
    width: calc(1080px - 64px); // Rmv to line up w/ padded hero text
    max-width: 100%;
    margin: 0 auto;
    color: ${(props) => props.theme.colors.brand[500]};
    font-size: 28px;
    font-weight: 500;
    margin-bottom: 0.25em;
    line-height: 125%;
    display: flex;
    justify-content: space-between;
    & > div {
      display: flex;
      align-items: center;
    }
    .scroll-reviews-back,
    .scroll-reviews-forward {
      height: 36px;
      width: 36px;
      border-radius: 18px;
      overflow: hidden;
      display: flex;
      justify-content: center;
      align-items: center;
      margin: 0 3px;
      border: 3px solid ${(props) => props.theme.colors.brand[700]};
      background: white;
      svg {
        height: 16px;
        width: 16px;
        path {
          fill: ${(props) => props.theme.colors.brand[500]};
        }
      }
      &:hover {
        border: 3px solid ${(props) => props.theme.colors.brand[500]};
        background: ${(props) => props.theme.colors.brand[500]};
        cursor: pointer;
        svg {
          path {
            fill: white;
          }
        }
      }
    }
    .scroll-reviews-back {
      margin-left: 20px;
      svg {
        transform: rotate(90deg);
      }
    }
    .scroll-reviews-forward {
      svg {
        transform: rotate(-90deg);
      }
    }
  }
  .reviews {
    padding: 1em 0.5em;
    display: grid;
    grid-auto-flow: column;
    grid-template-columns: repeat(3, 1fr);
    grid-template-rows: repeat(3, 1fr);
    column-gap: 0;
    row-gap: 0.75em;
    -ms-overflow-style: none;
    scrollbar-width: none;
    &::-webkit-scrollbar {
      display: none;
    }
    overflow-x: scroll;
  }

  @media (max-width: ${(props) => props.theme.breakpoints[500]}) {
    .review {
      height: 240px;
      width: 320px;
      min-width: auto;
      max-width: calc(100vw - 2em);
      .review__reviewer {
        flex-direction: column;
        justify-content: flex-start;
        align-items: flex-start;
      }
      .review__rating {
        justify-content: flex-start;
      }
    }
    .link-to-reviews {
      margin: 1em 0 0;
    }
  }
  @media (max-width: ${(props) => props.theme.breakpoints[700]}) {
    .reviews__controller {
      padding-left: 16px;
      padding-right: 16px;
      display: flex;
      flex-direction: column;
      .scroll-reviews-back {
        margin-left: 0;
        margin-top: 4px;
      }
    }
    .reviews {
      grid-template-rows: repeat(2, 1fr);
    }
  }
`;

export default ReviewsWall;
